import { useCallback, useEffect, useState } from "react";
import { parseApiError } from "utils/errors/parseApiError";

/**
 * The email-OTP gate, as the cardholder portal needs it. Ported from issuerApp's copy minus the
 * signup-leg classifier, which has no counterpart here.
 *
 * The subtle parts — which failures may be retried, how many digits, when resend unlocks — matter:
 * a wrong guess costs one of five server-side attempts, so a screen-local copy of this logic is a
 * screen-local way to burn them.
 */

/** Matches the backend's generateOtp. */
export const OTP_LENGTH = 5;

/** The server's own floor between sends is 60s; 120s keeps a normal resend clear of it. */
export const RESEND_COUNTDOWN_SECONDS = 120;

/**
 * Failures whose only recovery is a NEW code, never another guess.
 *
 * EMAIL_NOT_VERIFIED comes from the SAVE call rather than /verifyOtp: the address was verified but
 * its 30-minute window lapsed, or it was already spent. Branching only on the OTP codes misses it
 * and offers a retry that can never succeed.
 */
const NEEDS_NEW_CODE = new Set([
  "OTP_EXPIRED",
  "RATE_LIMITED",
  "EMAIL_NOT_VERIFIED",
]);

/** Digits only, capped — so the field can never hold a value the server rejects on shape. */
export const sanitizeOtp = (raw: string): string =>
  raw.replace(/\D/g, "").slice(0, OTP_LENGTH);

export interface OtpFailure {
  code: string;
  /** Display-ready text, already resolved by parseApiError. */
  message: string;
  /** Wrong digits. The guess is spent; clear the field so a retry cannot spend another. */
  isWrongCode: boolean;
  /** Dead code or guess cap — a fresh one is needed, not another attempt. */
  needsNewCode: boolean;
}

export function classifyOtpFailure(err: unknown, fallback: string): OtpFailure {
  const { code, message } = parseApiError(err, fallback);
  return {
    code,
    message,
    isWrongCode: code === "OTP_INVALID",
    needsNewCode: NEEDS_NEW_CODE.has(code),
  };
}

/**
 * Countdown to the next allowed resend.
 *
 * Deadline-based rather than decrement-based: a `setSecondsLeft(s - 1)` interval that is torn down
 * and rebuilt each tick drifts past the real cooldown, and stalls entirely while a background tab
 * is throttled. Comparing against a fixed deadline is immune to both.
 */
export function useResendCountdown(): {
  secondsLeft: number;
  start: () => void;
  stop: () => void;
} {
  const [deadline, setDeadline] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!deadline) {
      setSecondsLeft(0);
      return;
    }
    const tick = () =>
      setSecondsLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [deadline]);

  // Stable identities: an unstable start/stop leaks into every consumer's dependency arrays.
  const start = useCallback(
    () => setDeadline(Date.now() + RESEND_COUNTDOWN_SECONDS * 1000),
    [],
  );
  const stop = useCallback(() => setDeadline(0), []);

  return { secondsLeft, start, stop };
}

/** mm:ss for the "you can request a new code in …" line. */
export function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
